// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { dappAPI } from '@canton-network/dapp-sdk';
import {
  DefaultApi,
  DisclosedContract,
  EventFormat,
  Filters,
  GetLedgerEndResponse,
  GetPreferredPackageVersionResponse,
  JsContractEntry,
  JsContractEntryOneOf,
  TransactionFormatTransactionShapeEnum,
} from '@digitalasset/json-api-ts-client';
import hash from 'object-hash';
import { v4 as uuidv4 } from 'uuid';
import { AllocationRequest } from '@daml.js/splice-api-token-allocation-request/lib/Splice/Api/Token/AllocationRequestV1';
import { Allocation } from '@daml.js/splice-api-token-allocation/lib/Splice/Api/Token/AllocationV1';
import { Holding as TokenStandardHolding } from '@daml.js/splice-api-token-holding/lib/Splice/Api/Token/HoldingV1';
import { TransferInstruction } from '@daml.js/splice-api-token-transfer-instruction/lib/Splice/Api/Token/TransferInstructionV1';
import {
  CollateralAgreement,
  ExecutedCollateralTransfer,
  InstructedCollateral,
} from '@daml.js/utility-collateral-app-v1/lib/Utility/Collateral/App/Model/Collateral';
import {
  CollateralAgreement as CollateralAgreementV2,
  CollateralAgreement_SettlementObligation,
  CollateralAgreementChangeRequest as CollateralAgreementChangeRequestV2,
  CollateralAgreementRequest as CollateralAgreementRequestV2,
  ExecutedCollateralTransfer as ExecutedCollateralTransferV2,
  FailedCollateralTransfer,
  SettlementInstruction,
} from '@daml.js/utility-collateral-app-v2/lib/Utility/Collateral/App/Model/Collateral/module';
import { CommercialAgreement } from '@daml.js/utility-commercials-v0/lib/Utility/Commercials/V0/Model/CommercialAgreement';
import { DelegatedBatchedMarkersProxy } from '@daml.js/utility-commercials-v0/lib/Utility/Commercials/V0/Model/DelegatedBatchedMarkersProxy';
import { CommercialAgreementOffer } from '@daml.js/utility-commercials-v0/lib/Utility/Commercials/V0/Model/Offer';
import { UserService } from '@daml.js/utility-credential-app-v0/lib/Utility/Credential/App/V0/Service/User';
import { Credential } from '@daml.js/utility-credential-v0/lib/Utility/Credential/V0/Credential';
import { OperatorConfiguration } from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Configuration/Operator';
import { ProviderConfiguration } from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Configuration/Provider';
import { TransferPreapproval as RegistryTransferPreapproval } from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Model/TransferPreapproval';
import { AllocationFactory } from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Service/AllocationFactory';
import {
  ProviderService,
  ProviderServiceRequest,
} from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Service/Provider';
import {
  RegistrarService,
  RegistrarServiceRequest,
} from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Service/Registrar';
import { Holding } from '@daml.js/utility-registry-holding-v0/lib/Utility/Registry/Holding/V0/Holding';
import { AppRewardConfiguration } from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Configuration/AppReward';
import { InstrumentConfiguration } from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Configuration/Instrument';
import { TransferRule } from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Rule/Transfer';
import {
  Choice,
  ContractId,
  DisclosedContract as DamlDisclosedContract,
  InterfaceCompanion,
  List,
  Party,
  Template,
} from '@daml/types';
import { ACSDatabase, ACSDatabaseValue } from '../config/config';
import { PackageName } from '../config/package';
import { ACSCache, ACSCacheIndexedDB, ACSCacheLRU, ACSHelper } from './ACS';
import { ACSCacheOPFS } from './ACS/ACSCacheOPFS';
import InterfaceContract from './InterfaceContract';
import TemplateContract from './TemplateContract';
import { WSSupport } from './WSSupport';
import { ApiInstance } from './types';

type CreateEvent<T extends object, K = unknown, I extends string = string> = {
  templateId: I;
  contractId: ContractId<T>;
  signatories: List<Party>;
  observers: List<Party>;
  key: K;
  payload: T;
};

type CommandMeta = {
  workflowId?: string;
  disclosedContracts?: DamlDisclosedContract[];
  domainId?: string;
};

type SubmitterArgs =
  | {
      actAs?: Party[];
      readAs?: Party[];
      submitter?: never;
    }
  | {
      actAs?: never;
      readAs?: never;
      submitter?: Party;
    };

export type LedgerCreateArgs<T extends object> = {
  argument: T;
  meta?: CommandMeta;
} & SubmitterArgs;

export type LedgerExerciseArgs<T extends object, C> = {
  cid: ContractId<T>;
  argument: C;
  meta?: CommandMeta;
} & SubmitterArgs;

const getACSCache = (useCache: boolean, database: ACSDatabaseValue): ACSCache | null => {
  if (!useCache) {
    console.log('Cache disabled...');
    return null;
  }

  if (ACSCacheOPFS.canUse() && database === ACSDatabase.OPFS) {
    console.log('Instantiating OPFS cache...');
    return new ACSCacheOPFS();
  }

  if (ACSCacheIndexedDB.canUse() && database === ACSDatabase.IndexedDB) {
    console.log('Instantiating IndexedDB cache...');
    return new ACSCacheIndexedDB();
  }

  if (ACSCacheLRU.canUse() && database === ACSDatabase.LRU) {
    console.log('Instantiating LRU cache...');
    return new ACSCacheLRU();
  }

  console.log('Cache disabled...');
  return null;
};

export type QueryParameters = {
  offset: number;
  party: string;
};

// Uses the JSON API (via @daml/ledger) to connect to the ledger.
export default class LedgerApiClientV2 {
  private apiInstance: ApiInstance;

  private deduplicationDurationInSeconds: number;

  // needs to be marked with # in order to avoid serialization (LedgerApiClientV2 is serialized when used as key)
  #acsHelper: ACSHelper;

  constructor(
    apiInstance: ApiInstance,
    deduplicationDurationInSeconds: number,
    wsSupport: WSSupport,
    database: ACSDatabaseValue,
  ) {
    this.apiInstance = apiInstance;
    this.deduplicationDurationInSeconds = deduplicationDurationInSeconds;

    this.#acsHelper = new ACSHelper(
      this.apiInstance,
      wsSupport,
      // When user has disabled WebSocket supports we want to always refetch entire ACS instead of
      // trying to do incremental updates.
      getACSCache(wsSupport.enabled(), database),
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static logPreferredPackageError(packageName: string, operator: string, error: any) {
    console.warn(
      `No preferred package version retrieved for "${packageName}" (operator: ${operator}):`,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      typeof error === 'object' && 'message' in error ? error.message : error,
    );
  }

  async getCacheSize(): Promise<number> {
    return this.#acsHelper.getCacheSize();
  }

  async clearAllActiveContractsStateCache(): Promise<void> {
    await this.#acsHelper.clearAllCache();
  }

  async getPreferredPackageVersion(packageName: PackageName, operator: string, party: string) {
    if (this.isDappSdk()) {
      // SDK Mode: Manual construction of the query
      const params = new URLSearchParams();
      params.append('package-name', packageName);
      params.append('parties', operator);
      params.append('parties', party);

      const resource = `/v2/interactive-submission/preferred-package-version?${params.toString()}`;
      return (this.apiInstance as dappAPI.LedgerApi)({
        requestMethod: 'get',
        resource,
      })
        .then((r) => {
          return r as GetPreferredPackageVersionResponse;
        })
        .catch((error) => {
          LedgerApiClientV2.logPreferredPackageError(packageName, operator, error);
          return {};
        });
    }

    // Standard Mode: Use the generated method
    return (this.apiInstance as DefaultApi)
      .getV2InteractiveSubmissionPreferredPackageVersion(packageName, [operator, party])
      .catch((error) => {
        LedgerApiClientV2.logPreferredPackageError(packageName, operator, error);
        return {};
      });
  }

  api() {
    return this.apiInstance;
  }

  async queryTransferInstructions(
    params: QueryParameters,
  ): Promise<InterfaceContract<TransferInstruction>[]> {
    return this.queryInterface(params, TransferInstruction);
  }

  async queryCredentialUserServices(
    params: QueryParameters,
    operator: string,
  ): Promise<TemplateContract<UserService>[]> {
    const svcs = await this.query(params, UserService);
    return svcs.filter((r) => {
      const { payload } = r;
      return payload.operator === operator;
    });
  }

  async queryCredentials(params: QueryParameters): Promise<TemplateContract<Credential>[]> {
    return this.query(params, Credential);
  }

  async queryRegistryOperatorConfiguration(
    params: QueryParameters,
    operator: string,
  ): Promise<TemplateContract<OperatorConfiguration> | null> {
    const configs = await this.query(params, OperatorConfiguration);
    return (
      configs.find((r) => {
        const { payload } = r;
        return payload.operator === operator;
      }) || null
    );
  }

  async queryRegistryOperatorConfigurations(
    params: QueryParameters,
    operator: string,
  ): Promise<TemplateContract<OperatorConfiguration>[]> {
    const configs = await this.query(params, OperatorConfiguration);
    return configs.filter((r) => {
      const { payload } = r;
      return payload.operator === operator;
    });
  }

  async queryRegistryProviderConfiguration(
    params: QueryParameters,
    operator: string,
    provider: string,
  ): Promise<TemplateContract<ProviderConfiguration> | null> {
    const configs = await this.query(params, ProviderConfiguration);
    return (
      configs.find((r) => {
        const { payload } = r;
        return payload.operator === operator && payload.provider === provider;
      }) || null
    );
  }

  async queryRegistryProviderConfigurations(
    params: QueryParameters,
    operator: string,
  ): Promise<TemplateContract<ProviderConfiguration>[]> {
    const configs = await this.query(params, ProviderConfiguration);
    return configs.filter((r) => {
      const { payload } = r;
      return payload.operator === operator;
    });
  }

  async queryRegistryInstrumentConfigurations(
    params: QueryParameters,
    operator: string,
  ): Promise<TemplateContract<InstrumentConfiguration>[]> {
    const configs = await this.query(params, InstrumentConfiguration);
    return configs.filter((r) => {
      const { payload } = r;
      return payload.operator === operator;
    });
  }

  async queryDelegatedBatchedMarkersProxy(
    params: QueryParameters,
    operator: string,
    provider: string,
  ): Promise<TemplateContract<DelegatedBatchedMarkersProxy> | null> {
    const proxies = await this.query(params, DelegatedBatchedMarkersProxy);
    return (
      proxies.find((proxy) => {
        const { payload } = proxy;
        return payload.operator === operator && payload.provider === provider;
      }) || null
    );
  }

  async queryRegistryProviderServiceRequests(
    params: QueryParameters,
    operator: string,
  ): Promise<TemplateContract<ProviderServiceRequest>[]> {
    const reqs = await this.query(params, ProviderServiceRequest);
    return reqs.filter((r) => {
      const { payload } = r;
      return payload.operator === operator;
    });
  }

  async queryRegistryProviderServices(
    params: QueryParameters,
    operator: string,
  ): Promise<TemplateContract<ProviderService>[]> {
    const reqs = await this.query(params, ProviderService);
    return reqs.filter((r) => {
      const { payload } = r;
      return payload.operator === operator;
    });
  }

  async queryRegistryProviderService(
    params: QueryParameters,
    operator: string,
  ): Promise<TemplateContract<ProviderService> | null> {
    const svcs = await this.query(params, ProviderService);
    return (
      svcs.find((r) => {
        const { payload } = r;
        return payload.operator === operator && payload.provider === params.party;
      }) || null
    );
  }

  async queryRegistryRegistrarServiceRequests(
    params: QueryParameters,
    operator: string,
  ): Promise<TemplateContract<RegistrarServiceRequest>[]> {
    const reqs = await this.query(params, RegistrarServiceRequest);
    return reqs.filter((r) => {
      const { payload } = r;
      return payload.operator === operator;
    });
  }

  async queryRegistryRegistrarServices(
    params: QueryParameters,
    operator: string,
  ): Promise<TemplateContract<RegistrarService>[]> {
    const reqs = await this.query(params, RegistrarService);
    return reqs.filter((r) => {
      const { payload } = r;
      return payload.operator === operator;
    });
  }

  async queryRegistryRegistrarService(
    params: QueryParameters,
    operator: string,
  ): Promise<TemplateContract<RegistrarService> | null> {
    const svcs = await this.query(params, RegistrarService);
    return (
      svcs.find((r) => {
        const { payload } = r;
        return payload.operator === operator && payload.registrar === params.party;
      }) || null
    );
  }

  async queryHoldings(
    params: QueryParameters,
    operator: string,
  ): Promise<TemplateContract<Holding>[]> {
    const res = await this.query(params, Holding);
    return res.filter((h) => {
      const { payload } = h;
      return (
        payload.operator === operator &&
        (payload.owner === params.party ||
          payload.registrar === params.party ||
          payload.provider === params.party ||
          payload.operator === params.party)
      );
    });
  }

  async queryAppRewardConfiguration(
    params: QueryParameters,
    operator: string,
  ): Promise<TemplateContract<AppRewardConfiguration>[]> {
    const configs = await this.query(params, AppRewardConfiguration);
    return configs.filter((r) => {
      const { payload } = r;
      return payload.operator === operator;
    });
  }

  async queryInstrumentConfigurations(
    params: QueryParameters,
    operator: string,
  ): Promise<TemplateContract<InstrumentConfiguration>[]> {
    const res = await this.query(params, InstrumentConfiguration);
    return res.filter((r) => {
      const { payload } = r;
      return payload.operator === operator;
    });
  }

  async queryRegistryTransferPreapproval(
    params: QueryParameters,
    operator: string,
  ): Promise<TemplateContract<RegistryTransferPreapproval>[]> {
    const res = await this.query(params, RegistryTransferPreapproval);
    return res.filter((r) => {
      const { payload } = r;
      return payload.operator === operator;
    });
  }

  // Collateral

  async queryCollateralAgreements(
    params: QueryParameters,
    operator: string,
  ): Promise<TemplateContract<CollateralAgreement>[]> {
    const res = await this.query(params, CollateralAgreement);
    return res.filter((r) => r.payload.operator === operator);
  }

  async queryCollateralAgreementsV2(
    params: QueryParameters,
    operator: string,
  ): Promise<TemplateContract<CollateralAgreementV2>[]> {
    const res = await this.query(params, CollateralAgreementV2);
    return res.filter((r) => r.payload.operator === operator);
  }

  async queryCollateralAgreementSettlementObligations(
    params: QueryParameters,
    operator: string,
  ): Promise<TemplateContract<CollateralAgreement_SettlementObligation>[]> {
    const res = await this.query(params, CollateralAgreement_SettlementObligation);
    return res.filter((r) => r.payload.operator === operator);
  }

  async queryCollateralAgreementRequestsV2(
    params: QueryParameters,
    operator: string,
  ): Promise<TemplateContract<CollateralAgreementRequestV2>[]> {
    const res = await this.query(params, CollateralAgreementRequestV2);
    return res.filter((r) => r.payload.operator === operator);
  }

  async queryCollateralAgreementChangeRequestsV2(
    params: QueryParameters,
  ): Promise<TemplateContract<CollateralAgreementChangeRequestV2>[]> {
    const res = await this.query(params, CollateralAgreementChangeRequestV2);
    return res;
  }

  async queryExecutedCollateralTransferV2(
    params: QueryParameters,
    operator: string,
  ): Promise<TemplateContract<ExecutedCollateralTransferV2>[]> {
    const res = await this.query(params, ExecutedCollateralTransferV2);
    return res.filter((r) => r.payload.operator === operator);
  }

  async querySettlementInstructions(
    params: QueryParameters,
    operator: string,
  ): Promise<TemplateContract<SettlementInstruction>[]> {
    const res = await this.query(params, SettlementInstruction);
    return res.filter((r) => r.payload.operator === operator);
  }

  async queryFailedCollateralTransfersV2(
    params: QueryParameters,
    operator: string,
  ): Promise<TemplateContract<FailedCollateralTransfer>[]> {
    const res = await this.query(params, FailedCollateralTransfer);
    return res.filter((r) => r.payload.operator === operator);
  }

  async queryInstructedCollateral(
    params: QueryParameters,
    operator: string,
  ): Promise<TemplateContract<InstructedCollateral>[]> {
    const res = await this.query(params, InstructedCollateral);
    return res.filter((r) => r.payload.operator === operator);
  }

  async queryExecutedCollateralTransfer(
    params: QueryParameters,
    operator: string,
  ): Promise<TemplateContract<ExecutedCollateralTransfer>[]> {
    const res = await this.query(params, ExecutedCollateralTransfer);
    return res.filter((r) => r.payload.operator === operator);
  }

  // Commercial Agreements
  async queryCommercialAgreements(
    params: QueryParameters,
    operator: string,
  ): Promise<TemplateContract<CommercialAgreement>[]> {
    const res = await this.query(params, CommercialAgreement);
    return res.filter((c) => c.payload.operator === operator);
  }

  async queryCommercialAgreementOffers(
    params: QueryParameters,
    operator: string,
  ): Promise<TemplateContract<CommercialAgreementOffer>[]> {
    return (await this.query(params, CommercialAgreementOffer)).filter(
      (offer) => offer.payload.operator === operator,
    );
  }

  async queryTokenStandardHoldings(
    params: QueryParameters,
  ): Promise<InterfaceContract<TokenStandardHolding>[]> {
    return this.queryInterface(params, TokenStandardHolding);
  }

  async queryAllocationFactory(
    params: QueryParameters,
    operator: string,
    registrar: string,
  ): Promise<TemplateContract<AllocationFactory>[]> {
    const res = await this.query(params, AllocationFactory);
    return res.filter((c) => c.payload.operator === operator && c.payload.registrar === registrar);
  }

  async queryAllocations(params: QueryParameters): Promise<InterfaceContract<Allocation>[]> {
    return this.queryInterface(params, Allocation);
  }

  async queryAllocationRequestInterface(
    params: QueryParameters,
  ): Promise<InterfaceContract<AllocationRequest>[]> {
    return this.queryInterface(params, AllocationRequest);
  }

  async queryTransferRules(
    params: QueryParameters,
    operator: string,
    registrar: string,
  ): Promise<TemplateContract<TransferRule>[]> {
    const res = await this.query(params, TransferRule);
    return res.filter((c) => c.payload.operator === operator && c.payload.registrar === registrar);
  }

  private isDappSdk(): boolean {
    return typeof this.apiInstance === 'function';
  }

  async getLedgerEnd(): Promise<GetLedgerEndResponse> {
    if (this.isDappSdk()) {
      return (this.apiInstance as dappAPI.LedgerApi)({
        requestMethod: 'get',
        resource: '/v2/state/ledger-end',
      }).then((r) => {
        return r as GetLedgerEndResponse;
      });
    }

    // Standard Mode: Use the generated method
    return (this.apiInstance as DefaultApi).getV2StateLedgerEnd().then((r) => r);
  }

  async create<T extends object, K, I extends string>(
    template: Template<T, K, I>,
    argument: T,
    userId: string,
    meta?: CommandMeta,
    actAs: string[] = [],
    readAs: string[] = [],
  ): Promise<CreateEvent<T, K, I>> {
    const commandId = hash({
      templateId: template.templateId,
      argument,
      actAs,
      readAs,
    });

    const command = {
      CreateCommand: {
        templateId: template.templateId,
        createArguments: template.encode(argument),
      },
    };

    const commands = {
      commands: {
        commands: [command],
        workflowId: meta?.workflowId || '',
        userId,
        commandId,
        deduplicationPeriod: this.getDeduplicationDuration(),
        actAs,
        readAs,
        submissionId: uuidv4(),
        disclosedContracts: LedgerApiClientV2.toDisclosedContracts(meta),
        domainId: meta?.domainId || '',
        packageIdSelectionPreference: [],
      },
      transactionFormat: LedgerApiClientV2.getTransactionFormat(
        actAs,
        TransactionFormatTransactionShapeEnum.TRANSACTION_SHAPE_ACS_DELTA,
      ),
    };

    const response = await (
      this.apiInstance as DefaultApi
    ).postV2CommandsSubmitAndWaitForTransaction(commands);

    const event = response.transaction.events?.at(0);
    if (event && 'CreatedEvent' in event) {
      const createdEvent = event.CreatedEvent;
      return {
        templateId: template.templateId,
        contractId: createdEvent.contractId as ContractId<T>,
        signatories: createdEvent.signatories || [],
        observers: createdEvent.observers || [],
        key: createdEvent.contractKey as K,
        payload: template.decoder.runWithException(createdEvent.createArgument),
      };
    }
    return Promise.reject(new Error('CreatedEvent is expected from the response'));
  }

  async exercise<T extends object, C, R, K>(
    choice: Choice<T, C, R, K>,
    cid: ContractId<T>,
    argument: C,
    userId: string,
    meta?: CommandMeta,
    actAs: string[] = [],
    readAs: string[] = [],
  ): Promise<R> {
    const commandId = hash({
      contractId: cid,
      templateId: choice.template().templateId,
      choiceName: choice.choiceName,
      argument,
      actAs,
      readAs,
    });

    const command = {
      ExerciseCommand: {
        templateId: choice.template().templateId,
        contractId: cid,
        choice: choice.choiceName,
        choiceArgument: choice.argumentEncode(argument),
      },
    };

    const commands = {
      commands: {
        commands: [command],
        workflowId: meta?.workflowId || '',
        userId,
        commandId,
        deduplicationPeriod: this.getDeduplicationDuration(),
        actAs,
        readAs,
        submissionId: uuidv4(),
        disclosedContracts: LedgerApiClientV2.toDisclosedContracts(meta),
        domainId: meta?.domainId || '',
        packageIdSelectionPreference: [],
      },
      transactionFormat: LedgerApiClientV2.getTransactionFormat(
        actAs,
        TransactionFormatTransactionShapeEnum.TRANSACTION_SHAPE_LEDGER_EFFECTS,
      ),
    };

    const response = await (
      this.apiInstance as DefaultApi
    ).postV2CommandsSubmitAndWaitForTransaction(commands);

    const event = response.transaction.events?.at(0);
    if (event && 'ExercisedEvent' in event) {
      const exercisedEvent = event.ExercisedEvent;
      if ('exerciseResult' in exercisedEvent) {
        return choice.resultDecoder.runWithException(exercisedEvent.exerciseResult);
      }
    }

    return Promise.reject(new Error('ExercisedEvent is expected from the response'));
  }

  async query<T extends object, K, I extends string>(
    { party, offset }: QueryParameters,
    template: Template<T, K, I>,
  ): Promise<TemplateContract<T>[]> {
    return this.activeContractsForTemplate(offset, party, template.templateId).then((responses) => {
      return responses
        .map((resp) => resp.contractEntry)
        .filter(LedgerApiClientV2.isJsActiveContract)
        .map((activeContract) =>
          TemplateContract.fromJsonAPIV2(template, activeContract.JsActiveContract.createdEvent),
        );
    });
  }

  private async queryInterface<T extends object, K, I extends string>(
    { party, offset }: QueryParameters,
    interfaceCompanion: InterfaceCompanion<T, K, I>,
  ): Promise<InterfaceContract<T>[]> {
    return this.activeContractsForInterface(offset, party, interfaceCompanion.templateId).then(
      (responses) => {
        return responses
          .map((resp) => resp.contractEntry)
          .filter(LedgerApiClientV2.isJsActiveContract)
          .map((activeContract) =>
            InterfaceContract.fromJsonAPIV2(
              interfaceCompanion,
              activeContract.JsActiveContract.createdEvent,
            ),
          );
      },
    );
  }

  private static isJsActiveContract = (
    contract: JsContractEntry,
  ): contract is JsContractEntryOneOf => {
    return 'JsActiveContract' in contract;
  };

  async prepareCreate<T extends object, K, I extends string>(
    template: Template<T, K, I>,
    argument: T,
    userId: string,
    meta?: CommandMeta,
    actAs: string[] = [],
    readAs: string[] = [],
  ) {
    const commandId = hash({
      templateId: template.templateId,
      argument,
      actAs,
      readAs,
    });

    const command = {
      CreateCommand: {
        templateId: template.templateId,
        createArguments: template.encode(argument),
      },
    };

    const domains = await (this.apiInstance as DefaultApi).getV2StateConnectedSynchronizers(
      actAs[0],
    );
    const domain = domains.connectedSynchronizers ? domains.connectedSynchronizers[0] : undefined;

    const request = {
      userId,
      commandId,
      commands: [command],
      actAs,
      readAs,
      disclosedContracts: LedgerApiClientV2.toDisclosedContracts(meta),
      domainId: domain?.synchronizerId || '',
      synchronizerId: domain?.synchronizerId || '',
      packageIdSelectionPreference: [],
      verboseHashing: true,
    };

    return (this.apiInstance as DefaultApi).postV2InteractiveSubmissionPrepare(request);
  }

  async prepareExercise<T extends object, C, R, K>(
    choice: Choice<T, C, R, K>,
    cid: ContractId<T>,
    argument: C,
    userId: string,
    meta?: CommandMeta,
    actAs: string[] = [],
    readAs: string[] = [],
  ) {
    const commandId = hash({
      contractId: cid,
      templateId: choice.template().templateId,
      choiceName: choice.choiceName,
      argument,
      actAs,
      readAs,
    });

    const command = {
      ExerciseCommand: {
        templateId: choice.template().templateId,
        contractId: cid,
        choice: choice.choiceName,
        choiceArgument: choice.argumentEncode(argument),
      },
    };

    const domains = await (this.apiInstance as DefaultApi).getV2StateConnectedSynchronizers(
      actAs[0],
    );
    const domain = domains.connectedSynchronizers ? domains.connectedSynchronizers[0] : undefined;

    const request = {
      userId,
      commandId,
      commands: [command],
      actAs,
      readAs,
      disclosedContracts: LedgerApiClientV2.toDisclosedContracts(meta, domain?.synchronizerId),
      domainId: domain?.synchronizerId || '',
      synchronizerId: domain?.synchronizerId || '',
      packageIdSelectionPreference: [],
      verboseHashing: true,
    };
    return (this.apiInstance as DefaultApi).postV2InteractiveSubmissionPrepare(request);
  }

  async activeContractsForTemplate(offset: number, partyFilter: string, templateId: string) {
    return this.#acsHelper.activeContractsForTemplate(offset, partyFilter, templateId);
  }

  async activeContractsForInterface(offset: number, partyFilter: string, interfaceId: string) {
    return this.#acsHelper.activeContractsForInterface(offset, partyFilter, interfaceId);
  }

  async activeContracts(filters: Filters, offset: number, partyFilter: string | undefined) {
    const eventFormat: EventFormat = partyFilter
      ? {
          filtersByParty: {
            [partyFilter]: filters,
          },
          verbose: false,
        }
      : {
          filtersByParty: {},
          filtersForAnyParty: filters,
          verbose: false,
        };

    return (this.apiInstance as DefaultApi).postV2StateActiveContracts({
      activeAtOffset: offset,
      verbose: false,
      eventFormat,
    });
  }

  private static toDisclosedContracts(meta?: CommandMeta, domainId?: string): DisclosedContract[] {
    return (
      meta?.disclosedContracts?.map((disclosedContract) => ({
        ...disclosedContract,
        domainId: domainId || meta?.domainId || '',
        synchronizerId: domainId || meta?.domainId || '',
      })) || []
    );
  }

  private getDeduplicationDuration() {
    return {
      DeduplicationDuration: {
        value: {
          seconds: this.deduplicationDurationInSeconds,
          nanos: 0,
        },
      },
    };
  }

  private static getTransactionFormat(
    parties: string[],
    transactionShape: TransactionFormatTransactionShapeEnum,
  ) {
    const filtersByParties = parties.reduce(
      (acc, party) => ({
        ...acc,
        [party]: {
          cumulative: [
            {
              identifierFilter: {
                WildcardFilter: {
                  value: {
                    includeCreatedEventBlob: true,
                  },
                },
              },
            },
          ],
        },
      }),
      {},
    );

    return {
      eventFormat: {
        filtersByParty: filtersByParties,
        verbose: false,
      },
      transactionShape,
    };
  }
}
